import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../utils/api";
import { IoTrashBinSharp } from "react-icons/io5";
import SafeNumberInput from "../SafeNumberInput";
import { MdEdit } from "react-icons/md";
import { MdHideImage } from "react-icons/md";
import { ObservationEditor } from "../Editor";

interface IObservation {
  id: number;
  description: string;
  content: string;
  date: string | null;
}

interface IDataRequest {
  page: number;
  query: string;
  start_date: string;
  end_date: string;
}

interface IEditorState {
  id?: number;
  description?: string;
  content?: string;
  date?: string;
}

export const MobileObservations = () => {
  const navigate = useNavigate();
  const [observations, setObservations] = useState<IObservation[]>([]);
  const [requestParams, setRequestParams] = useState<IDataRequest>({
    page: 1,
    query: "",
    start_date: "",
    end_date: "",
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [globalHiddenImgs, setGlobalHiddenImgs] = React.useState<boolean>(true);
  const [editorState, setEditorState] = React.useState<IEditorState | null>(null);

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  async function submit() {
    if (requestParams.start_date && !dateRegex.test(requestParams.start_date)) {
      setErrorMessage("Start date must be in YYYY-MM-DD format");
      return;
    }
    if (requestParams.end_date && !dateRegex.test(requestParams.end_date)) {
      setErrorMessage("End date must be in YYYY-MM-DD format");
      return;
    }
    setErrorMessage("");
    setIsLoading(true);

    try {
      const resp = await api.get<IObservation[]>(`/observations`, {
        params: requestParams,
      });
      setObservations(resp.data);
    } catch (error: any) {
      console.error(error?.message ?? error);
      setErrorMessage("Failed to load observations");
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteObservation(id: number) {
    if (!window.confirm("Are you sure you want to delete this observation?")) {
      return;
    }

    setErrorMessage("");
    setIsLoading(true);

    try {
      await api.delete(`/observation/${id}`);
      setObservations((obs) => obs.filter((o) => o.id !== id));
    } catch (error: any) {
      console.error(error?.message ?? error);
      setErrorMessage("Failed to delete observation");
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading…</div>;
  }

  if (editorState !== null) {
    return (
      <ObservationEditor
        id={editorState.id}
        initialDescription={editorState.description ?? ""}
        initialContent={editorState.content ?? ""}
        initialDate={editorState.date ?? ""}
        onClose={() => setEditorState(null)}
      />
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="p-2 pb-10 overflow-auto h-full">
        <Link to="/mobile" className="btn btn-default mb-4">To Menu</Link>

        {errorMessage && <div className="text-red-500 mb-2">{errorMessage}</div>}

        <div className="flex items-center mb-2">
          <MdHideImage
            size={25}
            className="cursor-pointer"
            onClick={() => setGlobalHiddenImgs(!globalHiddenImgs)}
          />
          <span className="ml-2 text-sm text-gray-500">Toggle images</span>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex flex-col gap-2 mb-4"
        >
          <div className="form-group">
            <label htmlFor="page">Page:</label>
            <SafeNumberInput
              id="page"
              value={requestParams.page}
              onChange={(value) =>
                setRequestParams({ ...requestParams, page: value })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="query">Query:</label>
            <input
              placeholder="INTC"
              type="text"
              id="query"
              name="query"
              value={requestParams.query}
              onChange={(e) =>
                setRequestParams({ ...requestParams, query: e.target.value })
              }
            />
          </div>

          <div className="flex gap-2">
            <div className="form-group flex-1">
              <label htmlFor="start_date">From:</label>
              <input
                type="text"
                id="start_date"
                placeholder="YYYY-MM-DD"
                value={requestParams.start_date}
                onChange={(e) =>
                  setRequestParams({ ...requestParams, start_date: e.target.value })
                }
              />
            </div>
            <div className="form-group flex-1">
              <label htmlFor="end_date">To:</label>
              <input
                type="text"
                id="end_date"
                placeholder="YYYY-MM-DD"
                value={requestParams.end_date}
                onChange={(e) =>
                  setRequestParams({ ...requestParams, end_date: e.target.value })
                }
              />
            </div>
            <button type="submit" className="btn btn-default self-end">
              Get
            </button>
            <button
              type="button"
              className="btn btn-default self-end"
              onClick={() => setEditorState({})}
            >
              Add
            </button>
          </div>
        </form>

        <div className="terminal-timeline">
          {observations.map((obs) => (
            <MobileObsCard
              key={obs.id}
              obs={obs}
              deleteObservation={deleteObservation}
              onEdit={(state) => setEditorState(state)}
              globalHiddenImgs={globalHiddenImgs}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface IMobileObsCardProps {
  obs: IObservation;
  deleteObservation: (id: number) => void;
  onEdit: (state: IEditorState) => void;
  globalHiddenImgs: boolean;
}

const MobileObsCard = ({ obs, deleteObservation, onEdit, globalHiddenImgs }: IMobileObsCardProps) => {
  const [hiddenImgs, setHiddenImgs] = React.useState<boolean>(true);

  const dateLabel = obs.date
    ? `${obs.date} ${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(obs.date + "T00:00:00").getDay()]} — `
    : "";

  return (
    <div className="terminal-card">
      <header className="w-full flex justify-between">
        <div className="flex flex-col text-left ml-2">
          <div>{dateLabel}</div>
          <div>{obs.description}</div>
        </div>

        <div className="flex-col mr-4 items-center justify-center">
          <MdHideImage
            size={20}
            className="cursor-pointer mb-2"
            onClick={() => setHiddenImgs(!hiddenImgs)}
          />

          <MdEdit
            size={20}
            className="cursor-pointer mb-2"
            onClick={() =>
              onEdit({
                id: obs.id,
                description: obs.description,
                content: obs.content,
                date: obs.date ?? "",
              })
            }
          />

          <IoTrashBinSharp
            size={20}
            className="cursor-pointer block"
            onClick={() => deleteObservation(obs.id)}
          />
        </div>
      </header>
      <div
        className={hiddenImgs && globalHiddenImgs ? "container-with-hidden-imgs" : ""}
        dangerouslySetInnerHTML={{ __html: obs.content }}
      />
    </div>
  );
};
